/**
 * Payment bulk-search descriptor — adapts SearchPayments to BulkSearchResults.
 * The queried ids go in as paymentIdentifiers; status/state are server-side
 * PropertyFilter refinements.
 */
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiPaymentSummary,
  PaymentFilters,
  SearchPaymentsInput,
} from 'src/store/slices/searchPayments/searchPaymentsApi';
import { searchPayments, clearSearchPayments } from 'src/store/slices/searchPayments/searchPaymentsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchPaymentsInput => {
  const f: PaymentFilters = { paymentIdentifiers: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconPaymentStatus = status;
  if (state) f.beaconPaymentState = state;
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const paymentSearchDescriptor: BulkSearchDescriptor<ApiPaymentSummary> = {
  entity: 'payment',
  title: 'Bulk payment search',
  path: '/payments',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'End-to-end id',
      cell: (p) => <DetailLink to={`/payment?id=${encodeURIComponent(p.id)}`}>{p.id}</DetailLink>,
    },
    { id: 'beaconStatus', header: 'Beacon status', cell: (p) => <StatusCell state={p.beaconPaymentState} label={p.beaconPaymentStatus} /> },
    { id: 'paymentStatus', header: 'Payment status', cell: (p) => dash(p.paymentDetails?.paymentStatus) },
    { id: 'system', header: 'System', cell: (p) => dash(p.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (p) => (p.currentDirection ? titleCase(p.currentDirection) : '-') },
    { id: 'paymentMethod', header: 'Payment method', cell: (p) => dash(p.paymentDetails?.paymentMethod) },
    { id: 'bank', header: 'Bank', cell: (p) => dash(p.paymentDetails?.bank) },
    { id: 'updatedAt', header: 'Updated', cell: (p) => (p.updatedAt ? fullTimestamp(p.updatedAt) : '-') },
  ],
  run: (dispatch, args) => { dispatch(searchPayments({ input: buildInput(args) })); },
  clear: (dispatch) => { dispatch(clearSearchPayments()); },
  select: (s) => ({
    items: s.searchPayments.payments,
    total: s.searchPayments.total,
    loading: s.searchPayments.loading,
    error: s.searchPayments.error,
    regionResults: s.searchPayments.regionResults,
    searchedRegions: s.searchPayments.searchedRegions,
    regionErrors: s.searchPayments.regionErrors,
  }),
};
